import { reachGoal } from '../analytics';
import { DataLayerEvent, DataLayerEventType, DataLayerItemsListName, InitialParams, ViewListItem } from './types';

const DATA_LAYER_KEY = 'dataLayer';
const EMPTY_DATA_LAYER_EVENT: DataLayerEventType = {
	ecommerce: null
};

export class DataLayerAnalytics {
	private initParams: InitialParams;
	private commonParams: Partial<ViewListItem> = {};
	private readonly debugMode: boolean;

	public constructor(initParams: InitialParams = {}) {
		this.initParams = initParams;

		if (process.env.NODE_ENV === 'development') {
			this.debugMode = true;
		}
	}

	public static debugEvent(eventName: string, event: object) {
		console.log(`DATA_LAYER: ${eventName}`, event);
	}

	public static createInstance() {
		return new DataLayerAnalytics();
	}

	public sendDataLayerEvent(event: DataLayerEventType) {
		window[DATA_LAYER_KEY] = window[DATA_LAYER_KEY] || [];

		window[DATA_LAYER_KEY].push(event);

		if (this.debugMode) {
			DataLayerAnalytics.debugEvent(event?.event || 'Empty', event);
		}
	}

	public pushEmptyToDataLayer() {
		this.sendDataLayerEvent(EMPTY_DATA_LAYER_EVENT);
	}

	public sendVisitParameters(event: DataLayerEventType) {
		const visitParameters = {
			main: {
				[window.location.hostname]: {
					[event.event]: {
						ecommerce: {}
					}
				}
			},
			extra: {
				ecommerce: JSON.stringify(event.ecommerce)
			}
		};

		reachGoal(event.event, visitParameters);
	}

	public pushToDataLayer(
		event: DataLayerEvent,
		ecommerceItems: ViewListItem[] = null,
		params: object = {},
		itemsListName?: DataLayerItemsListName
	) {
		const eventObj: DataLayerEventType = {
			event,
			ecommerce: {
				item_list_name: itemsListName,
				coupon: this.initParams.coupon ?? null,
				currency: this.initParams.currency ?? null,
				items: ecommerceItems.map(item => ({
					...this.commonParams,
					...item
				})),
				...params
			}
		};

		this.addValueToEvent(eventObj);
		this.pushEmptyToDataLayer();
		this.sendDataLayerEvent(eventObj);
		this.sendVisitParameters(eventObj);
	}

	public init(params: InitialParams) {
		this.initParams = params;
	}

	public setCommonParams = (params: Partial<ViewListItem>) => {
		this.commonParams = {
			...this.commonParams,
			...params
		};
	};

	public addValueToEvent(eventObj: DataLayerEventType): DataLayerEventType {
		switch (eventObj.event) {
			case DataLayerEvent.AddToCart:
			case DataLayerEvent.ViewItem:
			case DataLayerEvent.Checkout:
			case DataLayerEvent.Purchase:
			case DataLayerEvent.RemoveFromCart:
				if (!eventObj?.ecommerce?.items) {
					return eventObj;
				}

				let value = 0;

				for (const item of eventObj.ecommerce.items) {
					if (item.price && item.quantity) {
						value += item.price * item.quantity;
					}
				}

				eventObj.ecommerce.value = value;

				return eventObj;
			default:
				return eventObj;
		}
	}
}

const instance = DataLayerAnalytics.createInstance();

export default instance;
